import React, { useState, useRef, useEffect } from "react";
import {
  Send,
  Paperclip,
  MoreHorizontal,
  Bot,
  User,
  BarChart3,
  MessageCircle,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Select, DatePicker, InputNumber, Button, message, Slider } from "antd";
import socketService from "../../services/socketService";
import { useLayout } from "../../contexts/LayoutContext";
import { MMM_JOB_CONFIG_ID, GEOLIFT_JOB_CONFIG_IDS } from "../../services/insightsService";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PerformanceAgentSteps from "../../components/agents/PerformanceAgent/PerformanceAgentSteps";
import { useParams, useSearchParams } from "react-router-dom";

// TypeScript Interfaces
interface Message {
  id: number;
  type: "user" | "ai";
  content: string;
  timestamp: Date;
  isFile?: boolean;
}

interface PrewrittenPrompt {
  title: string;
  prompt: string;
  response: string;
  triggerAnalysis?: boolean;
}
interface ProgressStep {
  progress: number;
  text: string;
}

interface StepMessages {
  [key: number]: ProgressStep[];
}

const ChatInterface: React.FC = () => {
  const { chatid } = useParams<{ chatid: string }>();
  const [, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showAnalysis, setShowAnalysis] = useState<boolean>(true);
  const [showInsightsLanding, setShowInsightsLanding] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Performance Agent flow detection - detect from chatid or from receiving PA events
  const [isPerformanceAgentFlow, setIsPerformanceAgentFlow] = useState<boolean>(
    chatid?.toLowerCase().includes("allocate media") ||
    chatid?.toLowerCase().includes("performance agent") ||
    false
  );

  // Resizable panel state - default 35% for chat, 65% for results
  const [leftWidth, setLeftWidth] = useState<number>(35);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const currentMessageRef = useRef<string>("");
  const currentMessageIdRef = useRef<number | null>(null);
  const processedTimestamps = useRef<Set<string>>(new Set()); // Prevent duplicate message processing
  const [visualizationData, setVisualizationData] = useState<any>(null);
  const [allStepsData, setAllStepsData] = useState<{ [key: number]: any }>({});
  const [currentStep, setCurrentStep] = useState<number>(1); // Start at step 1 by default

  // Performance Agent user input request state
  const [userInputRequest, setUserInputRequest] = useState<any>(null);

  // Performance Agent KPI/Objective selection state
  const [selectedObjective, setSelectedObjective] = useState<string>("");
  const [selectedKPI, setSelectedKPI] = useState<string>("");
  const [hasSubmittedSelection, setHasSubmittedSelection] =
    useState<boolean>(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState<boolean>(false);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);
  const [formData, setFormData] = useState<any>({});
  const { setSidebarExpanded } = useLayout();
  useEffect(() => {
    setSearchParams({ step: currentStep.toString() });
  }, [currentStep, setSearchParams]);
  useEffect(() => {
    const isButtonDisabled =
      isPerformanceAgentFlow &&
      !hasSubmittedSelection &&
      (!selectedObjective || !selectedKPI);
    console.log("🔘 Send Button State Check:", {
      isPerformanceAgentFlow,
      hasSubmittedSelection,
      selectedObjective,
      selectedKPI,
      isButtonDisabled,
    });
  }, [
    isPerformanceAgentFlow,
    hasSubmittedSelection,
    selectedObjective,
    selectedKPI,
  ]);

  // Helper functions for user/tenant management
  const getTenantId = (): string => {
    return localStorage.getItem("tenantId") || "default-tenant";
  };

  const getUserId = (): string => {
    return localStorage.getItem("userId") || "default-user";
  };

  const prewrittenPrompts: PrewrittenPrompt[] = [
    {
      title: "MMM Analysis",
      prompt: "Run MMM analysis on our current campaigns",
      response:
        "I've analyzed your media mix model attribution. Digital channels contribute 35% of attribution, followed by TV at 25%. The analysis shows strong digital performance with optimization opportunities in traditional media allocation.",
      triggerAnalysis: true,
    },
    {
      title: "Campaign Performance",
      prompt: "Show me campaign performance trends",
      response:
        "Your campaign performance shows steady growth. Conversions increased 92% from January to June (120 to 230), with consistent impression growth indicating healthy campaign scaling. The trend suggests strong momentum heading into Q3.",
      triggerAnalysis: true,
    },
    {
      title: "Incrementality Testing",
      prompt: "Help with incrementality testing setup",
      response:
        "I'll help set up incrementality testing with control and treatment groups. We should use geographic or audience-based splits to ensure clean measurement of your campaign effectiveness.",
      triggerAnalysis: true,
    },
    {
      title: "General Question",
      prompt: "What are best practices for marketing attribution?",
      response:
        "Marketing attribution best practices include: implementing multi-touch attribution models, combining first-party and third-party data, using statistical methods like MMM for holistic view, conducting incrementality tests for validation, and maintaining consistent measurement frameworks across channels.",
      triggerAnalysis: false,
    },
  ];

  // Initialize Socket.IO connection
  useEffect(() => {
    const userId = getUserId();
    const tenantId = getTenantId();

    // Get backend URL and remove /api suffix if present for Socket.IO connection
    let backendUrl =
      process.env.REACT_APP_API_URL ||
      process.env.BACKEND_API_URL ||
      "http://localhost:7001";
    backendUrl = backendUrl.replace(/\/api$/, ""); // Remove trailing /api
    backendUrl = backendUrl.replace("0.0.0.0", "localhost"); // Replace 0.0.0.0 with localhost for browser

    console.log("Initializing Socket.IO connection...", {
      userId,
      tenantId,
      backendUrl,
    });

    // Connect to Socket.IO
    socketService.connect(userId, tenantId, backendUrl);

    socketService.on({
      onConnect: () => {
        console.log("Socket.IO connected successfully");
        setIsConnected(true);
        setConnectionError(null);

        // Add catch-all debug listener after connection
        const socket = (socketService as any).socket;
        if (socket) {
          socket.onAny((eventName: string, ...args: any[]) => {
            console.log(`🔔 Socket event received: "${eventName}"`, args);
          });
        }

        // Add welcome message
        const initialMessage: Message = {
          id: Date.now(),
          type: "ai",
          content:
            "Hi! I’m your AI-powered Performance Agent.I use your historical marketing data to optimize and measure marketing strategies. How can I assist you today?",
          timestamp: new Date(),
        };
        setMessages([initialMessage]);
      },
      onDisconnect: () => {
        console.log("Socket.IO disconnected");
        setIsConnected(false);
      },
      onMessageChunk: (data) => {
        console.log("Received message chunk:", data.chunk);
        currentMessageRef.current += data.chunk;
        setMessages((prev) => {
          // Find existing AI message being built (last AI message)
          const lastAiIndex = prev.map(m => m.type).lastIndexOf("ai");
          const lastAiMessage = lastAiIndex >= 0 ? prev[lastAiIndex] : null;

          // If we have an AI message that matches our current build, update it
          if (lastAiMessage && lastAiMessage.id === currentMessageIdRef.current) {
            return prev.map((msg) =>
              msg.id === currentMessageIdRef.current
                ? { ...msg, content: currentMessageRef.current }
                : msg
            );
          } else {
            // Create new AI message for this response
            const newId = Date.now();
            currentMessageIdRef.current = newId;
            const newMessage: Message = {
              id: newId,
              type: "ai",
              content: currentMessageRef.current,
              timestamp: new Date(),
            };
            return [...prev, newMessage];
          }
        });

        setIsTyping(true);
      },
      onMessageComplete: (data) => {
        console.log("📨 onMessageComplete:", data.message?.substring(0, 50), "chunksReceived:", currentMessageRef.current.length > 0);

        const chunksWereReceived = currentMessageRef.current.length > 0;

        // If chunks were received, the message is already displayed - DO NOT touch messages state
        // Only add a new message if NO chunks were received (direct response)
        if (!chunksWereReceived && data.message && data.message.trim()) {
          setMessages((prev) => {
            // Check if we already have this exact content (safety check)
            const lastAiMessage = [...prev].reverse().find(msg => msg.type === "ai");
            if (lastAiMessage && lastAiMessage.content.trim() === data.message.trim()) {
              return prev; // Already have it
            }
            return [...prev, {
              id: Date.now(),
              type: "ai" as const,
              content: data.message,
              timestamp: new Date(),
            }];
          });
        }

        // Reset refs
        currentMessageRef.current = "";
        currentMessageIdRef.current = null;
        setIsTyping(false);

        // Check if we should trigger analysis based on metadata
        if (data.metadata?.trigger_analysis) {
          setShowAnalysis(true);
        }
      },
      onError: (error) => {
        console.error("Socket.IO error:", error);
        setConnectionError(error.error);
        setIsTyping(false);

        // Add error message to chat
        const errorMessage: Message = {
          id: Date.now(),
          type: "ai",
          content: `Error: ${error.error}. Please try again.`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      },
      onStatusUpdate: (status) => {
        console.log("Status update:", status);
        // Could show status in UI if needed
      },
      onToolExecution: (tool) => {
        console.log("Tool execution:", tool);
        // Could show tool execution status in UI
      },
      // Performance Agent visualization handlers
      onVisualizationUpdate: (data: any) => {
        console.log("📊 Visualization update received:", {
          type: data.type,
          step: data.step,
          dataKeys: Object.keys(data.data || {}),
          fullData: data
        });

        // Activate Performance Agent flow when receiving PA visualization events
        setIsPerformanceAgentFlow(true);

        setVisualizationData(data);
        const stepNumber = data.step || 0;
        setCurrentStep(stepNumber);

        // Store data for this step to persist it
        setAllStepsData(prev => {
          const updated = {
            ...prev,
            [stepNumber]: data
          };
          console.log("📦 Updated allStepsData:", Object.keys(updated));
          return updated;
        });

        // Auto-show analysis panel when visualization data arrives
        if (!showAnalysis) {
          console.log("🎨 Showing analysis panel");
          setShowAnalysis(true);
        }
      },
      onStepProgress: (data: any) => {
        console.log("📈 Step progress received:", {
          current_step: data.current_step,
          total_steps: data.total_steps,
          step_name: data.step_name,
          status: data.status
        });
        setCurrentStep(data.current_step);
      },
      onRequestUserInput: (data: any) => {
        console.log("📝 User input requested:", {
          step: data.step,
          input_type: data.input_type,
          message: data.input_data?.message
        });

        // DEDUPLICATION: Skip if we've already processed this input request
        const inputKey = data.timestamp || `${data.step}-${data.input_type}`;
        if (processedTimestamps.current.has(inputKey)) {
          console.log("⚠️ Duplicate request_user_input detected, skipping:", inputKey);
          return;
        }
        processedTimestamps.current.add(inputKey);

        // Add AI message asking for input (so it persists in chat)
        if (data.input_data?.message) {
          const aiMessage: Message = {
            id: Date.now(),
            type: "ai",
            content: data.input_data.message,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, aiMessage]);
        }

        setUserInputRequest(data);
      },
    });

    // Cleanup on unmount
    return () => {
      console.log("Cleaning up Socket.IO connection");
      socketService.disconnect();
    };
  }, []);

  // Handle resize dragging
  const handleMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!isDragging || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const newLeftWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain between 20% and 80%
      if (newLeftWidth >= 20 && newLeftWidth <= 80) {
        setLeftWidth(newLeftWidth);
      }
    };

    const handleMouseUp = (): void => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Control sidebar state based on analysis panel state
  useEffect(() => {
    if (showAnalysis) {
      setSidebarExpanded(false);
    }

    // Cleanup: restore expanded sidebar when component unmounts
    return () => {
      setSidebarExpanded(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const scrollToBottom = (): void => {
    // messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handler for user response from Performance Agent input forms
  const handleUserResponse = (inputType: string, value: any) => {
    if (!userInputRequest) return;

    console.log("📤 Sending user response:", { inputType, value });

    // Format the user's response as a readable message
    let responseText = "";
    if (inputType === 'step1_kpi_selection') {
      responseText = `Selected KPI: ${value.kpi}`;
    } else if (inputType === 'step1_date_input') {
      responseText = `Campaign Duration: ${value.start_date} to ${value.end_date}`;
    } else if (inputType === 'step1_budget_input') {
      if (value.budget) {
        responseText = `Budget: $${value.budget.toLocaleString()}`;
      } else if (value.kpi_target) {
        responseText = `KPI Target: ${value.kpi_target.toLocaleString()}`;
      }
    } else if (inputType.includes('_approval')) {
      responseText = value.action === 'approve' ? 'Approved ✓' : 'Rejected';
    }

    // Add user's response to chat messages
    if (responseText) {
      const userMessage: Message = {
        id: Date.now(),
        type: "user",
        content: responseText,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
    }

    // Send user response via Socket.IO
    socketService.sendUserResponse({
      session_id: userInputRequest.session_id,
      step: userInputRequest.step,
      input_type: inputType,
      value: value
    });

    // Clear the input request (form will disappear but message stays)
    setUserInputRequest(null);
  };

  // Render user input form based on backend request
  const renderUserInputForm = () => {
    if (!userInputRequest) return null;

    const { input_type, input_data } = userInputRequest;

    // KPI Selection
    if (input_type === 'step1_kpi_selection') {
      return (
        <div>
          <h4 className="text-base font-semibold mb-3">
            {input_data.message}
          </h4>
          <Select
            className="w-full"
            placeholder="Select KPI"
            size="large"
            options={input_data.input_fields.kpi.options.map((opt: string) => ({
              label: opt,
              value: opt
            }))}
            onChange={(value) => {
              handleUserResponse(input_type, { kpi: value });
            }}
          />
        </div>
      );
    }

    // Date Range Input
    if (input_type === 'step1_date_input') {
      return (
        <div>
          <h4 className="text-base font-semibold mb-3">
            {input_data.message}
          </h4>
          <DatePicker.RangePicker
            className="w-full"
            size="large"
            disabledDate={(current) => {
              // Disable all dates before today
              return current && current.isBefore(new Date(), 'day');
            }}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                // Additional validation: ensure end date is after start date
                if (dates[1].isAfter(dates[0])) {
                  handleUserResponse(input_type, {
                    start_date: dates[0].format('YYYY-MM-DD'),
                    end_date: dates[1].format('YYYY-MM-DD')
                  });
                } else {
                  message.error('End date must be after start date');
                }
              }
            }}
          />
        </div>
      );
    }

    // Budget Input with Slider
    if (input_type === 'step1_budget_input') {
      const budgetField = input_data?.input_fields?.budget || {};
      const budgetConstraints = budgetField.constraints || {};

      const originalBudget = budgetConstraints.original_budget || budgetField.default || 100000;
      const minBudget = budgetConstraints.min_budget || originalBudget * 0.5;
      const maxBudget = budgetConstraints.max_budget || originalBudget * 2.0;

      const currentBudget = formData.budget || originalBudget;
      const currentPercentage = Math.round((currentBudget / originalBudget) * 100);

      const sliderMarks: Record<number, string> = {
        [Math.round(minBudget)]: '50%',
        [Math.round(originalBudget)]: '100%',
        [Math.round(maxBudget)]: '200%',
      };

      return (
        <div>
          <h4 className="text-base font-semibold mb-3">
            {input_data.message}
          </h4>
          <div className="space-y-4">
            {/* Budget Section with Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">
                  {budgetField.label || 'Budget ($)'}
                </label>
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  currentPercentage < 100
                    ? 'bg-yellow-100 text-yellow-800'
                    : currentPercentage > 100
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {currentPercentage}% of original
                </span>
              </div>

              {/* Original Budget Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Original Budget:</span>
                  <span className="font-semibold">${Math.round(originalBudget).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1 text-gray-500">
                  <span>Range: ${Math.round(minBudget).toLocaleString()} - ${Math.round(maxBudget).toLocaleString()}</span>
                  <span>(50% - 200%)</span>
                </div>
              </div>

              {/* Slider */}
              <div className="px-1 mb-3">
                <Slider
                  min={Math.round(minBudget)}
                  max={Math.round(maxBudget)}
                  step={Math.max(1, Math.round(originalBudget * 0.01))}
                  value={Math.round(currentBudget)}
                  marks={sliderMarks}
                  tooltip={{ formatter: (value) => `$${value?.toLocaleString()}` }}
                  onChange={(value) => setFormData({ ...formData, budget: value })}
                />
              </div>

              {/* Input Number */}
              <InputNumber
                className="w-full"
                size="large"
                placeholder={budgetField.placeholder || 'Enter budget'}
                min={Math.round(minBudget)}
                max={Math.round(maxBudget)}
                value={Math.round(currentBudget)}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => Number(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                onChange={(value) => setFormData({ ...formData, budget: value })}
              />
            </div>

            <div className="text-center text-gray-500 dark:text-gray-400 font-medium">OR</div>

            {/* KPI Target */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {input_data?.input_fields?.kpi_target?.label || 'KPI Target'}
              </label>
              <InputNumber
                className="w-full"
                size="large"
                placeholder={input_data?.input_fields?.kpi_target?.placeholder || 'Enter target'}
                min={input_data?.input_fields?.kpi_target?.min}
                onChange={(value) => setFormData({ ...formData, kpi_target: value })}
              />
            </div>

            {input_data.validation_message && (
              <p className="text-sm text-orange-600 dark:text-orange-400">
                {input_data.validation_message}
              </p>
            )}

            <Button
              type="primary"
              size="large"
              className="w-full"
              onClick={() => {
                // Use originalBudget as default if user didn't interact with slider
                const budgetToSend = formData.budget ?? originalBudget;
                if (budgetToSend || formData.kpi_target) {
                  handleUserResponse(input_type, { ...formData, budget: budgetToSend });
                  setFormData({});
                }
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      );
    }

    // Approval buttons (for plan, segments, experiment)
    if (input_type.includes('_approval')) {
      return (
        <div>
          <h4 className="text-base font-semibold mb-3">
            {input_data.message}
          </h4>
          <div className="flex gap-3">
            <Button
              type="primary"
              size="large"
              className="flex-1"
              onClick={() => handleUserResponse(input_type, { action: 'approve' })}
            >
              Approve
            </Button>
            <Button
              size="large"
              className="flex-1"
              onClick={() => handleUserResponse(input_type, { action: 'reject' })}
            >
              Reject
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clear messages and reset state when chatid changes (new conversation)
  useEffect(() => {
    console.log("📝 Chat ID changed, resetting conversation:", chatid);
    setMessages([{
      id: Date.now(),
      type: "ai",
      content: "Hi! I'm your Marketing Science AI assistant. I can help you run MMM analysis, synthetic control experiments, and find insights in your campaign data. What would you like to explore today?",
      timestamp: new Date(),
    }]);
    setAllStepsData({});
    setCurrentStep(1);
    processedTimestamps.current.clear(); // Clear deduplication cache on new conversation
    setIsTyping(false);
  }, [chatid]);

  // Auto-send chatid as initial message when socket connects
  useEffect(() => {
    if (isConnected && chatid && messages.length === 1) {
      // Decode the chatid from URL (e.g., "Allocate%20media..." -> "Allocate media...")
      const decodedMessage = decodeURIComponent(chatid);
      console.log("🚀 Auto-sending initial message from URL:", decodedMessage);

      // Wait a brief moment for socket to be fully ready
      setTimeout(() => {
        handleSendMessage(decodedMessage);
      }, 500);
    }
  }, [isConnected, chatid, messages.length]);

  const handleSendMessage = (message?: string): void => {
    let messageToSend = message || inputValue;

    // For Performance Agent flow, construct message from KPI/Objective if no text provided
    if (
      isPerformanceAgentFlow &&
      !messageToSend.trim() &&
      selectedObjective &&
      selectedKPI
    ) {
      messageToSend = `I want to optimize my campaign with objective: ${selectedObjective} and KPI: ${selectedKPI}`;
      console.log("📊 Constructed Performance Agent message:", messageToSend);
    }

    if (!messageToSend.trim()) return;

    // Hide insights landing page when user starts chatting
    if (showInsightsLanding) {
      setShowInsightsLanding(false);
    }

    // Check if connected
    if (!socketService.isConnected()) {
      console.error("Socket.IO not connected. Cannot send message.");
      const errorMessage: Message = {
        id: Date.now(),
        type: "ai",
        content: "Connection error. Please refresh the page and try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    // Add user message to UI
    const userMessage: Message = {
      id: Date.now(),
      type: "user",
      content: messageToSend,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = messageToSend;
    setInputValue("");
    currentMessageRef.current = "";
    currentMessageIdRef.current = Date.now() + 1;
    console.log("Sending message to backend:", messageText);

    // Include KPI/Objective in context for Performance Agent
    const context = isPerformanceAgentFlow
      ? {
          timestamp: new Date().toISOString(),
          objective: selectedObjective,
          kpi: selectedKPI,
          mmm_job_config_id: MMM_JOB_CONFIG_ID,
          geolift_job_config_id: GEOLIFT_JOB_CONFIG_IDS[0],
        }
      : {
          timestamp: new Date().toISOString(),
        };

    socketService.sendMessage(messageText, context);
    setIsTyping(true);

    // Mark selection as submitted for Performance Agent flow
    if (
      isPerformanceAgentFlow &&
      selectedObjective &&
      selectedKPI &&
      !hasSubmittedSelection
    ) {
      setHasSubmittedSelection(true);
      setIsLoadingAnalysis(true);
      setAnalysisProgress(0);
      // TODO: Remove this mock progressive loading once backend is ready
      // Simulate progressive analysis steps
      const analysisSteps = [
        { step: "Connecting to data sources...", progress: 15, delay: 800 },
        {
          step: "Fetching campaign performance data...",
          progress: 30,
          delay: 1200,
        },
        { step: "Running MMM attribution model...", progress: 50, delay: 1500 },
        {
          step: "Calculating channel ROI and incrementality...",
          progress: 70,
          delay: 1200,
        },
        {
          step: "Generating optimization recommendations...",
          progress: 100,
          delay: 1000,
        },
      ];

      let currentDelay = 0;
      analysisSteps.forEach((stepInfo, index) => {
        currentDelay += stepInfo.delay;
        setTimeout(() => {
          setAnalysisProgress(stepInfo.progress);
        }, currentDelay);
      });

      // Show final results after all steps complete
      setTimeout(() => {
        setAnalysisProgress(100);

        // Wait a brief moment before showing results
        setTimeout(() => {
          setIsLoadingAnalysis(false);
          const mockData = {
            type: "channel_recommendations",
            step: 1,
            data: {
              channels: {
                Meta: {
                  mmm_roi: 1.4,
                  sc_incrementality: 78,
                  current_spend: 50000,
                  recommended_spend: 65000,
                  expected_lift: 12000,
                },
                Google: {
                  mmm_roi: 0.78,
                  sc_incrementality: 45,
                  current_spend: 75000,
                  recommended_spend: 60000,
                  expected_lift: 8000,
                },
              },
              total_recommended_spend: 125000,
              total_expected_lift: 20000,
            },
          };
          setVisualizationData(mockData);
          setAllStepsData(prev => ({
            ...prev,
            1: mockData
          }));
          setHasSubmittedSelection(false);
          setCurrentStep(1);
        }, 500);
      }, currentDelay + 800);
    }
  };

  const handlePromptClick = (prompt: PrewrittenPrompt): void => {
    if (!socketService.isConnected()) {
      console.error("Socket.IO not connected. Cannot send message.");
      return;
    }

    const userMessage: Message = {
      id: Date.now(),
      type: "user",
      content: prompt.prompt,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsMenuOpen(false);
    currentMessageRef.current = "";
    currentMessageIdRef.current = Date.now() + 1;

    // Send message to backend via Socket.IO
    console.log("Sending prompt to backend:", prompt.prompt);
    socketService.sendMessage(prompt.prompt, {
      timestamp: new Date().toISOString(),
      is_preset: true,
    });

    // Set typing indicator
    setIsTyping(true);
  };

  const handleFileUpload = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      const fileMessage: Message = {
        id: Date.now(),
        type: "user",
        content: `📎 Uploaded file: ${file.name}`,
        timestamp: new Date(),
        isFile: true,
      };
      setMessages((prev) => [...prev, fileMessage]);
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const data = [
    { name: "Google", sales: 4000, profit: 2400 },
    { name: "Meta", sales: 3000, profit: 1398 },
  ];
  const stepMessages: StepMessages = {
    1: [
      { progress: 15, text: "Connecting to data sources" },
      { progress: 30, text: "Fetching campaign performance data" },
      { progress: 50, text: "Running MMM attribution model" },
      { progress: 70, text: "Calculating channel ROI and incrementality" },
      { progress: 100, text: "Generating optimization recommendations" },
    ],
    2: [
      { progress: 15, text: "Analyzing audience segments" },
      { progress: 30, text: "Calculating customer lifetime value" },
      { progress: 50, text: "Mapping channel × audience matrix" },
      { progress: 70, text: "Identifying high-value segments" },
      { progress: 100, text: "Building audience value recommendations" },
    ],
    3: [
      { progress: 15, text: "Initializing experiment framework" },
      { progress: 30, text: "Selecting test and control markets" },
      { progress: 50, text: "Calculating statistical power" },
      { progress: 70, text: "Setting up measurement parameters" },
      { progress: 100, text: "Finalizing experiment configuration" },
    ],
    4: [
      { progress: 15, text: "Connecting to media platforms" },
      { progress: 30, text: "Validating campaign parameters" },
      { progress: 50, text: "Deploying audience segments" },
      { progress: 70, text: "Activating media campaigns" },
      { progress: 100, text: "Campaign activation complete" },
    ],
  };
  const currentMessages: any[] = stepMessages[currentStep] || stepMessages[1];

  return (
    <div
      ref={containerRef}
      className="flex h-[calc(100vh-75px)] overflow-hidden bg-gray-50 dark:bg-gray-900 relative"
    >
      {/* Left Panel - Chat Interface */}
      <div
        className="flex flex-col border-r border-gray-200 dark:border-gray-700"
        style={{
          width: showAnalysis ? `${leftWidth}%` : "100%",
          transition: isDragging ? "none" : "width 0.2s ease",
        }}
      >
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 pb-2 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Performance Agent
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Live results from AI analysis
                </p>
              </div>
            </div>
            {/* Connection Status */}
            <div className="flex items-center space-x-2">
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-green-600 dark:text-green-400">
                    Connected
                  </span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-red-600 dark:text-red-400">
                    Disconnected
                  </span>
                </>
              )}
            </div>
          </div>
          {connectionError && (
            <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-1 rounded">
              {connectionError}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
          {/* <div ref={messagesEndRef} /> */}

          {messages.map((message: Message) => (
            <div
              key={message.id}
              className={`flex ${
                message.type === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`flex space-x-3 max-w-3xl ${
                  message.type === "user"
                    ? "flex-row-reverse space-x-reverse"
                    : ""
                }`}
              >
                {/* Avatar */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === "user"
                      ? "bg-blue-500"
                      : "bg-gradient-to-r from-purple-500 to-pink-500"
                  }`}
                >
                  {message.type === "user" ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>

                {/* Message */}
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    message.type === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <p className="whitespace-pre-wrap">
                    {message.content}
                    {isTyping &&
                      message.type === "ai" &&
                      message.id === currentMessageIdRef.current && (
                        <span className="inline-block w-0.5 h-5 bg-current ml-0.5 animate-pulse"></span>
                      )}
                  </p>
                  <div
                    className={`text-xs mt-2 ${
                      message.type === "user"
                        ? "text-blue-100"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Backend-driven User Input Forms - Show as AI message in chat */}
          {userInputRequest && (
            <div className="flex justify-start">
              <div className="flex space-x-3 max-w-3xl w-full">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-r from-purple-500 to-pink-500">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 flex-1">
                  {renderUserInputForm()}
                </div>
              </div>
            </div>
          )}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="flex space-x-3 max-w-3xl">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-r from-purple-500 to-pink-500">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleFileUpload}
              className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors"
              title="Upload file"
            >
              <Paperclip className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
              accept="*/*"
            />

            <div className="flex-1 relative">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                className="w-full px-4 py-2 pr-12 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                rows={1}
                style={{ minHeight: "40px", maxHeight: "120px" }}
              />
              <button
                onClick={() => {
                  console.log("🔘 Send button clicked:", {
                    inputValue: inputValue.trim(),
                    isPerformanceAgentFlow,
                    selectedObjective,
                    selectedKPI,
                  });
                  handleSendMessage();
                }}
                disabled={!inputValue.trim()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 rounded-lg flex items-center justify-center transition-colors disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>

            <div className="relative">
              <button
                onMouseEnter={() => setIsMenuOpen(true)}
                onMouseLeave={() => setIsMenuOpen(false)}
                className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors"
                title="More options"
              >
                <MoreHorizontal className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>

              {isMenuOpen && (
                <div
                  className="absolute bottom-full right-0 mb-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10"
                  onMouseEnter={() => setIsMenuOpen(true)}
                  onMouseLeave={() => setIsMenuOpen(false)}
                >
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Quick Prompts
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Click to use a pre-written prompt
                    </p>
                  </div>
                  <div className="p-2">
                    {prewrittenPrompts.map((prompt: any, index: number) => (
                      <button
                        key={index}
                        onClick={() => handlePromptClick(prompt)}
                        className="w-full text-left p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                      >
                        <div className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                          {prompt.title}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {prompt.prompt}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Resizer Divider */}
      {showAnalysis && (
        <div
          className="w-1 bg-gray-300 dark:bg-gray-600 hover:bg-blue-500 dark:hover:bg-blue-500 cursor-col-resize transition-colors relative group"
          onMouseDown={handleMouseDown}
          style={{ userSelect: "none" }}
        >
          {/* Visual indicator on hover */}
          <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-500/20" />
        </div>
      )}

      {/* Right Panel - Analysis Results / Performance Agent Steps */}
      {showAnalysis && (
        <div
          className="hidden md:flex flex-col max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800"
          style={{
            width: `${100 - leftWidth}%`,
            transition: isDragging ? "none" : "width 0.2s ease",
          }}
        >
          {isPerformanceAgentFlow ? (
            // Performance Agent Flow - Show Step Visualization and MMM Results
            <div className="overflow-y-auto h-full">
              {/* Always show the Ant Design stepper - user input forms are shown in chat (left panel) */}
              <PerformanceAgentSteps
                currentStep={currentStep}
                allStepsData={allStepsData}
                stepData={visualizationData}
              />

              {/* Show analysis progress - persists even after completion */}
              {hasSubmittedSelection && (
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="space-y-2">
                    {currentMessages.map((step, index) => {
                      const isCompleted = analysisProgress >= step.progress;
                      const isActive =
                        isLoadingAnalysis &&
                        analysisProgress >=
                          (currentMessages[index - 1]?.progress || 0) &&
                        analysisProgress < step.progress;

                      return (
                        <div
                          key={index}
                          className="flex items-center space-x-2 text-sm"
                        >
                          <div
                            className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isCompleted
                                ? "bg-green-500"
                                : isActive
                                ? "bg-blue-500 animate-pulse"
                                : "bg-gray-300"
                            }`}
                          >
                            {isCompleted && (
                              <span className="text-white text-xs">✓</span>
                            )}
                          </div>
                          <span
                            className={
                              isCompleted
                                ? "text-gray-900 dark:text-white"
                                : "text-gray-500 dark:text-gray-400"
                            }
                          >
                            {step.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Visualizations are now shown inside PerformanceAgentSteps */}
            </div>
          ) : (
            // Regular Analysis Results
            <>
              <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Analysis Results
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowAnalysis(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto h-full">
                <div className="m-auto ml-20 mt-5">
                  <ResponsiveContainer width="80%" height={350}>
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />

                      {/* Only two bars here */}
                      <Bar dataKey="sales" fill="#8884d8" />
                      <Bar dataKey="profit" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
